import { Alert, Box, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import {
  FeedbackSnackbar,
  FormDialog,
  FormTextField,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  type ResourceCollectionState,
  ValueBadge,
} from '../../../design-system';
import {
  CommonCatalogFields,
  commonDraftValid,
  emptyCommonDraft,
  type CommonCatalogDraft,
} from './ReferenceCatalogFields';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, CatalogDetailDialog, useReferenceLifecycle } from './ReferenceLifecycle';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  replaceCatalogItem,
  replaceCatalogItems,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  type ReferenceDataResource,
  type ReferenceSettingsCommonProps,
} from '../model/referenceData';
import { referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type { ReferenceJobLevel, ReferenceJobLevelChangeInput, ReferenceJobLevelCreateInput } from '../model/types';
type JobLevelDraft = CommonCatalogDraft & {
  sortOrder: string;
};
type JobLevelEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceJobLevel;
  draft: JobLevelDraft;
};
function jobLevelDraftValid(draft: JobLevelDraft, mode: JobLevelEditor['mode']) {
  const sortOrder = Number(draft.sortOrder);
  return commonDraftValid(draft, mode) && Number.isInteger(sortOrder) && sortOrder >= 0 && sortOrder <= 100000;
}
export function JobLevelReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  resource,
}: ReferenceSettingsCommonProps & {
  resource: ReferenceDataResource<ReferenceJobLevel>;
}) {
  const [items, setItems] = useState<ReferenceJobLevel[]>(resource.data ?? []);
  const [selected, setSelected] = useState<ReferenceJobLevel>();
  const [editor, setEditor] = useState<JobLevelEditor>();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.jobLevels(), []);
  useEffect(
    () =>
      setItems(
        [...(resource.data ?? [])].sort(
          (left, right) => left.sortOrder - right.sortOrder || left.descriptor.localeCompare(right.descriptor, 'ko'),
        ),
      ),
    [resource.data],
  );
  const filteredItems = useMemo(
    () =>
      filterCatalog(items, query, status).sort(
        (left, right) => left.sortOrder - right.sortOrder || left.descriptor.localeCompare(right.descriptor, 'ko'),
      ),
    [items, query, status],
  );
  const columns = useMemo<GridColDef<ReferenceJobLevel>[]>(
    () => [
      {
        field: 'descriptor',
        renderCell: ({ row }) => <ValueBadge label={row.descriptor} />,
        headerName: '직무 레벨',
        flex: 1,
        minWidth: 170,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 150,
      },
      {
        field: 'sortOrder',
        headerName: '정렬 순서',
        width: 110,
        type: 'number',
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [],
  );
  const updateItem = (item: ReferenceJobLevel) => {
    setItems((current) => replaceCatalogItem(current, item));
    setSelected((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) resource.reload();
  };
  const lifecycle = useReferenceLifecycle<ReferenceJobLevel>({
    kindLabel: '직무 레벨',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceJobLevel(id, input as ReferenceJobLevelChangeInput, key),
    fixtureBlockers: {
      'job-level-l1': [
        {
          type: 'WORKER_JOB',
          count: 7,
        },
      ],
    },
    onChanged: updateItem,
    onNotice: setNotice,
  });
  const openCreate = () => {
    const nextSortOrder = items.length ? Math.max(...items.map((item) => item.sortOrder)) + 10 : 10;
    setEditor({
      mode: 'create',
      draft: {
        ...emptyCommonDraft(),
        sortOrder: String(nextSortOrder),
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openEdit = (item: ReferenceJobLevel) => {
    setSelected(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        sortOrder: String(item.sortOrder),
        effectiveDate: item.status === 'SCHEDULED' ? item.effectiveFrom : todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeDraft = <K extends keyof JobLevelDraft>(field: K, value: JobLevelDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !jobLevelDraftValid(editor.draft, editor.mode) || busy) return;
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceJobLevel>;
    if (editor.mode === 'create') {
      const payload: ReferenceJobLevelCreateInput = {
        descriptor: editor.draft.descriptor.trim(),
        sortOrder: Number(editor.draft.sortOrder),
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `job-level-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            sortOrder: payload.sortOrder,
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceJobLevel(payload, key);
    } else {
      const payload: ReferenceJobLevelChangeInput = {
        version: editor.item!.version,
        descriptor: editor.draft.descriptor.trim(),
        sortOrder: Number(editor.draft.sortOrder),
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            sortOrder: payload.sortOrder ?? editor.item!.sortOrder,
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceJobLevel(editor.item!.id, payload, key);
    }
    const savedMode = editor.mode;
    void request
      .then(
        (saved) => {
          updateItem(saved);
          setEditor(undefined);
          setNotice(`${saved.descriptor} 직무 레벨을 ${savedMode === 'create' ? '등록' : '변경'}했습니다.`);
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '직무 레벨 정보를 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const collectionState: ResourceCollectionState = resource.loading
    ? {
        kind: 'loading',
        label: '직무 레벨 목록을 불러오는 중입니다.',
      }
    : resource.error
      ? {
          kind: 'error',
          message: resource.error,
          onRetry: resource.reload,
          retryLabel: '직무 레벨 목록 다시 시도',
        }
      : items.length === 0
        ? {
            kind: 'empty',
            title: '등록된 직무 레벨이 없습니다.',
            description: canWrite ? '직무 레벨 등록을 눌러 첫 항목을 추가해 주세요.' : '등록된 직무 레벨이 없습니다.',
          }
        : {
            kind: 'ready',
          };
  return (
    <>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      <ResourcePage
        title="직무 레벨"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "직무 레벨 등록",
          onCreate: openCreate,
          onBulkCreate: () => setBulkOpen(true),
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          {!canRead ? (
            <Stack
              spacing={1.5}
              sx={{
                mb: canRead ? 1.5 : 0,
                flexShrink: 0,
              }}
            >
              {!canRead ? (
                <Alert severity="warning">직무 레벨 기준정보를 조회하려면 staffing.read 권한이 필요합니다.</Alert>
              ) : null}
            </Stack>
          ) : null}
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={collectionState}
                rows={filteredItems}
                totalCount={items.length}
                columns={[...columns, catalogActionsColumn({ canWrite, onInspect: setSelected, onEdit: openEdit, onLifecycle: lifecycle.open })]}
                query={query}
                onQueryChange={setQuery}
                searchPlaceholder="직무 레벨 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '상태',
                    value: status,
                    onChange: setStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openEdit(row) : setSelected(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <CatalogDetailDialog
        item={selected}
        kindLabel="직무 레벨"
        canWrite={canWrite}
        canEdit={selected?.status !== 'SCHEDULED'}
        editDisabledReason={
          selected?.status === 'SCHEDULED' ? '사용 예정인 직무 레벨 속성은 적용일 이후 수정할 수 있습니다.' : undefined
        }
        extraItems={selected ? [['정렬 순서', String(selected.sortOrder)]] : []}
        onClose={() => setSelected(undefined)}
        onEdit={() => selected && openEdit(selected)}
        onLifecycle={(action) => selected && lifecycle.open(action, selected)}
      />

      <FormDialog
        open={Boolean(editor)}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        title={editor?.mode === 'create' ? '직무 레벨 등록' : '직무 레벨 수정'}
        cancelLabel="취소"
        submitLabel="변경사항 저장"
        busy={busy}
        busyLabel="저장 중…"
        submitDisabled={!editor || !jobLevelDraftValid(editor.draft, editor.mode)}
        maxWidth="sm"
      >
        {editor ? (
          <Stack spacing={2}>
            {editorError ? (
              <Alert severity="error" role="alert">
                {editorError}
              </Alert>
            ) : null}
            <CommonCatalogFields
              draft={editor.draft}
              mode={editor.mode}
              busy={busy}
              descriptorHelper="승진·보상과 인사 배정에 사용할 공식 단계 이름입니다."
              onChange={changeDraft}
            />
            <FormTextField
              required
              type="number"
              label="정렬 순서"
              value={editor.draft.sortOrder}
              disabled={busy}
              error={
                !Number.isInteger(Number(editor.draft.sortOrder)) ||
                Number(editor.draft.sortOrder) < 0 ||
                Number(editor.draft.sortOrder) > 100000
              }
              helperText="목록과 선택창에 표시할 순서를 0 이상 100000 이하의 정수로 입력해 주세요."
              onChange={(event) => changeDraft('sortOrder', event.target.value)}
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100000,
                  step: 1,
                },
              }}
            />
          </Stack>
        ) : null}
      </FormDialog>
      <ReferenceBulkCreateDialog<ReferenceJobLevel>
        open={bulkOpen}
        workflow={bulkWorkflow}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.jobLevel(row);
          return fixtureMode
            ? Promise.resolve({
                id: `job-level-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                sortOrder: payload.sortOrder,
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceJobLevel(payload, key);
        }}
        onCreated={(created) => {
          setItems((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) resource.reload();
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `직무 레벨 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `직무 레벨 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {lifecycle.dialog}
    </>
  );
}
